/**
 * Host-Aware API and WebSocket Gateway Resolver for Admin Dashboard
 * ADR-110 & STORY-EPIC-75.2
 */

export interface GatewayEndpoints {
  apiBaseUrl: string;
  wsBaseUrl: string;
}

export function resolveAuthoritativeApiBaseUrl(hostnameOverride?: string): string {
  const hostname = (
    hostnameOverride ||
    (typeof window !== 'undefined' && window.location ? window.location.hostname : '')
  ).toLowerCase();

  // 1. Localhost and IP development / PM2 preview mode (uses Vite Reverse Proxy)
  if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1') {
    const envApiUrl = import.meta.env.VITE_API_URL;
    if (envApiUrl && envApiUrl.startsWith('http')) {
      return envApiUrl;
    }
    return '/api/v1';
  }

  // 2. UAE Sovereign Admin Domain
  if (hostname.includes('gffdxb') || hostname.includes('uae')) {
    return 'https://api.gffdxb.com/api/v1';
  }

  // 3. Pakistan Sovereign Admin Domain
  if (hostname.includes('gffpk') || hostname.includes('pk')) {
    return 'https://api.gffpk.com/api/v1';
  }

  // 4. Fallback to explicit env var or relative path
  if (import.meta.env.VITE_API_URL && !import.meta.env.VITE_API_URL.startsWith('/')) {
    return import.meta.env.VITE_API_URL;
  }

  return '/api/v1';
}

export function resolveAuthoritativeSocketUrl(hostnameOverride?: string): string {
  const hostname = (
    hostnameOverride ||
    (typeof window !== 'undefined' && window.location ? window.location.hostname : '')
  ).toLowerCase();

  // 1. UAE Sovereign Admin Domain (returns standard https:// for Socket.io HTTP polling initiation)
  if (hostname.includes('gffdxb') || hostname.includes('uae')) {
    return 'https://api.gffdxb.com';
  }

  // 2. Pakistan Sovereign Admin Domain (returns standard https:// for Socket.io HTTP polling initiation)
  if (hostname.includes('gffpk') || hostname.includes('pk')) {
    return 'https://api.gffpk.com';
  }

  // 3. Localhost and IP development fallback
  if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1') {
    const envApiUrl = import.meta.env.VITE_API_URL;
    if (envApiUrl && envApiUrl.startsWith('http')) {
      return envApiUrl.replace(/\/api\/v1\/?$/, '');
    }
    if (!hostnameOverride && typeof window !== 'undefined' && window.location && window.location.origin) {
      return window.location.origin;
    }
    return 'http://localhost:4000';
  }

  // 4. Fallback from resolveAuthoritativeApiBaseUrl
  const apiBase = resolveAuthoritativeApiBaseUrl(hostname);
  if (apiBase.startsWith('http')) {
    return apiBase.replace(/\/api\/v1\/?$/, '');
  }

  return 'http://localhost:4000';
}

export function getGatewayEndpoints(hostnameOverride?: string): GatewayEndpoints {
  return {
    apiBaseUrl: resolveAuthoritativeApiBaseUrl(hostnameOverride),
    wsBaseUrl: resolveAuthoritativeSocketUrl(hostnameOverride),
  };
}
